import type { Session, User } from '@supabase/supabase-js';
import { supabase } from './supabaseClient';

export type AuthCallbackParams = {
  accessToken: string | null;
  refreshToken: string | null;
  code: string | null;
  type: string | null;
  errorCode: string | null;
  errorDescription: string | null;
};

export type AuthCallbackFailureKind =
  | 'error'
  | 'expired'
  | 'missing'
  | 'wrong_type'
  | 'pkce_unsupported'
  | 'incomplete';

export type AuthCallbackFailure = {
  ok: false;
  kind: AuthCallbackFailureKind;
  message: string;
};

export type ConfirmationSessionResult = { ok: true } | AuthCallbackFailure;

export type RecoverySessionResult =
  | { ok: true; user: User; session: Session }
  | AuthCallbackFailure;

export type PasswordUpdateResult = { ok: true } | AuthCallbackFailure;

const CONFIRMATION_TYPES = new Set(['signup', 'email', 'invite', 'magiclink']);

const EMPTY_PARAMS: AuthCallbackParams = {
  accessToken: null,
  refreshToken: null,
  code: null,
  type: null,
  errorCode: null,
  errorDescription: null,
};

/** In-memory one-shot guard so React remounts cannot re-apply the same credentials. */
let confirmationConsumedKey: string | null = null;
let recoveryConsumedKey: string | null = null;
let passwordUpdateInFlight: Promise<PasswordUpdateResult> | null = null;

export function __resetAuthCallbackControllersForTests(): void {
  confirmationConsumedKey = null;
  recoveryConsumedKey = null;
  passwordUpdateInFlight = null;
}

function decodeErrorDescription(value: string | null): string | null {
  if (!value) return null;
  const plusAsSpace = value.replace(/\+/g, ' ');
  try {
    return decodeURIComponent(plusAsSpace);
  } catch {
    return plusAsSpace;
  }
}

function credentialKey(params: AuthCallbackParams): string | null {
  if (!params.accessToken || !params.refreshToken) return null;
  // Length + short prefix only — never store or return full tokens.
  return `${params.type ?? ''}:${params.accessToken.length}:${params.refreshToken.length}:${params.accessToken.slice(0, 6)}`;
}

function failureFromErrorFields(params: AuthCallbackParams): AuthCallbackFailure | null {
  if (!params.errorCode && !params.errorDescription) return null;
  const description = decodeErrorDescription(params.errorDescription);
  const expired = params.errorCode === 'otp_expired';
  return {
    ok: false,
    kind: expired ? 'expired' : 'error',
    message:
      description ||
      (expired ? 'This link has expired.' : 'This link is invalid.'),
  };
}

function requireImplicitTokenPair(params: AuthCallbackParams): AuthCallbackFailure | null {
  if (params.code && !(params.accessToken && params.refreshToken)) {
    return {
      ok: false,
      kind: 'pkce_unsupported',
      message:
        'This link cannot be completed across browsers. Request a new email from MovieMe and open it in the same browser.',
    };
  }

  const hasAccess = Boolean(params.accessToken);
  const hasRefresh = Boolean(params.refreshToken);
  if (hasAccess !== hasRefresh) {
    return {
      ok: false,
      kind: 'incomplete',
      message: 'This link is incomplete. Request a new email from MovieMe.',
    };
  }
  if (!hasAccess || !hasRefresh) {
    return {
      ok: false,
      kind: 'missing',
      message: 'This link is invalid or has already been used.',
    };
  }
  return null;
}

/**
 * Pure parser for Supabase auth email callbacks.
 * Never log the input — fragments may contain access/refresh tokens.
 */
export function parseAuthEmailCallback(input?: string | URL): AuthCallbackParams {
  try {
    let search = '';
    let hash = '';

    if (input) {
      const url = typeof input === 'string' ? new URL(input, 'https://www.moviemeapp.com') : input;
      search = url.search.startsWith('?') ? url.search.slice(1) : url.search;
      hash = url.hash.startsWith('#') ? url.hash.slice(1) : url.hash;
    } else if (typeof window !== 'undefined') {
      search = window.location.search.startsWith('?')
        ? window.location.search.slice(1)
        : window.location.search;
      hash = window.location.hash.startsWith('#')
        ? window.location.hash.slice(1)
        : window.location.hash;
    }

    const hashParams = new URLSearchParams(hash);
    const queryParams = new URLSearchParams(search);
    const get = (key: string) => hashParams.get(key) || queryParams.get(key);

    return {
      accessToken: get('access_token'),
      refreshToken: get('refresh_token'),
      code: queryParams.get('code') || hashParams.get('code'),
      type: get('type'),
      errorCode: get('error_code') || get('error'),
      errorDescription: get('error_description'),
    };
  } catch {
    return { ...EMPTY_PARAMS };
  }
}

/** @deprecated Use parseAuthEmailCallback */
export const parseAuthCallbackParams = parseAuthEmailCallback;

export function clearAuthCallbackUrl(pathname: '/email-confirmed' | '/reset-password'): void {
  if (typeof window === 'undefined') return;
  window.history.replaceState({}, '', pathname);
}

/**
 * Confirmation/signup only. Requires a complete implicit token pair.
 * On success: clears the sensitive URL, then local-scope sign-out.
 * Never falls back to an existing browser session.
 */
export async function establishConfirmationSession(
  params: AuthCallbackParams
): Promise<ConfirmationSessionResult> {
  const fromErrors = failureFromErrorFields(params);
  if (fromErrors) return fromErrors;

  if (params.type === 'recovery') {
    return {
      ok: false,
      kind: 'wrong_type',
      message: 'This link is for password reset, not email confirmation.',
    };
  }

  if (!params.type || !CONFIRMATION_TYPES.has(params.type)) {
    return {
      ok: false,
      kind: 'wrong_type',
      message: 'This confirmation link is invalid or incomplete.',
    };
  }

  const pairError = requireImplicitTokenPair(params);
  if (pairError) return pairError;

  const key = credentialKey(params);
  if (key && confirmationConsumedKey === key) {
    return {
      ok: false,
      kind: 'missing',
      message: 'This link has already been used.',
    };
  }

  try {
    const { data, error } = await supabase.auth.setSession({
      access_token: params.accessToken!,
      refresh_token: params.refreshToken!,
    });

    if (error) {
      return { ok: false, kind: 'error', message: error.message };
    }

    if (!data.session?.user) {
      return {
        ok: false,
        kind: 'error',
        message: 'Could not verify this confirmation link.',
      };
    }

    if (key) confirmationConsumedKey = key;
    clearAuthCallbackUrl('/email-confirmed');

    // Email is verified; clear only this browser's temporary session.
    await supabase.auth.signOut({ scope: 'local' }).catch(() => undefined);

    return { ok: true };
  } catch {
    return {
      ok: false,
      kind: 'error',
      message: 'Could not complete confirmation from this link.',
    };
  }
}

/**
 * Recovery only. Requires type=recovery and a complete implicit token pair.
 * Returns the exact user/session from setSession. Never uses getSession fallback.
 */
export async function establishRecoverySession(
  params: AuthCallbackParams
): Promise<RecoverySessionResult> {
  const fromErrors = failureFromErrorFields(params);
  if (fromErrors) return fromErrors;

  if (params.type !== 'recovery') {
    return {
      ok: false,
      kind: 'wrong_type',
      message:
        params.type && CONFIRMATION_TYPES.has(params.type)
          ? 'This link is for email confirmation, not password reset.'
          : 'This password reset link is invalid or incomplete.',
    };
  }

  const pairError = requireImplicitTokenPair(params);
  if (pairError) return pairError;

  const key = credentialKey(params);
  if (key && recoveryConsumedKey === key) {
    return {
      ok: false,
      kind: 'missing',
      message: 'This link has already been used.',
    };
  }

  try {
    const { data, error } = await supabase.auth.setSession({
      access_token: params.accessToken!,
      refresh_token: params.refreshToken!,
    });

    if (error) {
      return { ok: false, kind: 'error', message: error.message };
    }

    const session = data.session;
    const user = data.user ?? data.session?.user ?? null;
    if (!session || !user) {
      return {
        ok: false,
        kind: 'error',
        message: 'Could not start a password reset session from this link.',
      };
    }

    if (key) recoveryConsumedKey = key;
    clearAuthCallbackUrl('/reset-password');

    return { ok: true, user, session };
  } catch {
    return {
      ok: false,
      kind: 'error',
      message: 'Could not open this password reset link.',
    };
  }
}

/**
 * Mutate password once the recovery session is established.
 * On success: local-scope sign-out. Concurrent submits share one in-flight request.
 */
export async function completePasswordUpdate(password: string): Promise<PasswordUpdateResult> {
  if (passwordUpdateInFlight) {
    return passwordUpdateInFlight;
  }

  const run = (async (): Promise<PasswordUpdateResult> => {
    try {
      const { data, error } = await supabase.auth.updateUser({ password });
      if (error) {
        return { ok: false, kind: 'error', message: error.message };
      }
      if (!data.user) {
        return {
          ok: false,
          kind: 'error',
          message: 'Password update did not return a user.',
        };
      }

      await supabase.auth.signOut({ scope: 'local' }).catch(() => undefined);
      return { ok: true };
    } catch {
      return {
        ok: false,
        kind: 'error',
        message: 'Could not update password. Try again.',
      };
    }
  })();

  passwordUpdateInFlight = run;
  try {
    return await run;
  } finally {
    if (passwordUpdateInFlight === run) {
      passwordUpdateInFlight = null;
    }
  }
}
